import { trackEvent } from "@/lib/analytics";
import { useState } from "react";
import SEO from "@/components/SEO";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ClipboardCheck, Workflow, GraduationCap, ShieldCheck, CheckCircle2, ArrowRight, Building2 } from "lucide-react";
import { Link } from "@/lib/router-compat";
import { useLanguage } from "@/i18n/LanguageContext";
import { useLocalizedRoute } from "@/hooks/useLocalizedRoute";

const schema = z.object({
  full_name: z.string().trim().min(2).max(100),
  role: z.string().trim().min(2).max(100),
  company: z.string().trim().min(2).max(120),
  store_count: z.string().min(1),
  email: z.string().trim().email().max(255),
});

const PILLARS_ES = [
  {
    icon: ClipboardCheck,
    title: "Auditoría de Red",
    desc: "Diagnóstico operativo, financiero y comercial tienda a tienda. Identificamos fugas de margen, ineficiencias y oportunidades ocultas en toda la red.",
    n: "01",
  },
  {
    icon: Workflow,
    title: "Estandarización de Procesos (SOPs)",
    desc: "Diseñamos los manuales operativos que convierten tu know-how en un sistema replicable. Cada apertura ejecuta el mismo estándar de excelencia.",
    n: "02",
  },
  {
    icon: GraduationCap,
    title: "Capacitación de Area Managers",
    desc: "Programa de formación intensivo para mandos intermedios. Pasan de apagar fuegos a liderar resultados con KPIs claros y rutinas de control.",
    n: "03",
  },
];

const PILLARS_EN = [
  {
    icon: ClipboardCheck,
    title: "Network Audit",
    desc: "Operational, financial and commercial diagnosis store by store. We identify margin leaks, inefficiencies and hidden opportunities across the network.",
    n: "01",
  },
  {
    icon: Workflow,
    title: "Process Standardization (SOPs)",
    desc: "We design the operating manuals that turn your know-how into a replicable system. Every opening executes the same standard of excellence.",
    n: "02",
  },
  {
    icon: GraduationCap,
    title: "Area Manager Training",
    desc: "Intensive training program for middle management. They move from firefighting to leading results with clear KPIs and control routines.",
    n: "03",
  },
];

export default function CadenasFranquicias() {
  const { lang } = useLanguage();
  useLocalizedRoute();
  const es = lang === "es";
  const [form, setForm] = useState({ full_name: "", role: "", company: "", store_count: "", email: "" });
  const [errors, setErrors] = useState<Record<string, boolean>>({});
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const PILLARS = es ? PILLARS_ES : PILLARS_EN;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const result = schema.safeParse(form);
    if (!result.success) {
      const errs: Record<string, boolean> = {};
      result.error.issues.forEach((i) => { errs[i.path[0] as string] = true; });
      setErrors(errs);
      return;
    }
    setErrors({});
    setSending(true);

    const message = `[Cadenas y Franquicias]\nCargo: ${form.role}\nEmpresa: ${form.company}\nNº de tiendas: ${form.store_count}\n\nSolicita Auditoría Operativa de su red.`;

    await supabase.from("contact_leads" as any).insert({
      full_name: form.full_name.trim(),
      email: form.email.trim(),
      profile_type: "retail_chain",
      message,
      status: "new",
      source: "chains_landing",
    });
    trackEvent("generate_lead", { lead_type: "cadenas_franquicias", store_count_range: form.store_count });

    setSending(false);
    setSent(true);
  };

  return (
    <div className="min-h-screen bg-[#0B0F14] text-white">
      <SEO
        title={es
          ? "Consultoría para Cadenas y Franquicias Retail | Retail Lemon"
          : "Consulting for Retail Chains and Franchises | Retail Lemon"}
        description={es
          ? "Estandarizamos la operativa de tu cadena de tiendas: auditoría de red, SOPs, capacitación de Area Managers y rollout de mejoras para escalar con rentabilidad."
          : "We standardize your retail chain operations: network audit, SOPs, Area Manager training, and rollout of improvements to scale profitably."}
        keywords={es
          ? "consultoría cadenas retail, franquicias retail, área manager, auditoría de tiendas, SOP retail, rollout retail"
          : "retail chain consulting, retail franchise, area manager, store audit, retail SOP, retail rollout"}
        path={lang === "en" ? "/en/chains-and-franchises" : "/cadenas-y-franquicias"}
        pathEs="/cadenas-y-franquicias"
        pathEn="/en/chains-and-franchises"
        lang={lang as "es" | "en"}
        type="website"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Service",
            serviceType: "Retail Chain & Franchise Consulting",
            name: es
              ? "Consultoría para Cadenas y Franquicias Retail"
              : "Retail Chain and Franchise Consulting",
            provider: {
              "@type": "Organization",
              name: "Retail Lemon",
              url: "https://retaillemon.com",
            },
            areaServed: { "@type": "Country", name: es ? "España" : "Spain" },
            audience: {
              "@type": "BusinessAudience",
              audienceType: "Retail chain operators, franchisors, area managers",
            },
            description: es
              ? "Auditoría de red, SOPs operativos, formación de Area Managers y rollout de mejoras para cadenas y franquicias retail."
              : "Network audit, operational SOPs, Area Manager training, and rollout of improvements for retail chains and franchises.",
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Retail Lemon", item: "https://retaillemon.com/" },
              { "@type": "ListItem", position: 2, name: es ? "Cadenas y Franquicias" : "Chains & Franchises", item: "https://retaillemon.com/cadenas-y-franquicias" },
            ],
          },
        ]}
      />

      {/* Top utility bar */}
      <div className="border-b border-white/5">
        <section className="container max-w-6xl py-4 flex items-center justify-between">
          <Link to="/" className="font-display text-lg font-bold tracking-tight">
            RETAIL<span className="text-foreground">LEMON</span>
          </Link>
          <span className="text-[11px] uppercase tracking-[0.25em] text-white/50">
            Corporate Solutions · Retail Operations
          </span>
        </section>
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.18]"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=1920&q=80')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
          aria-hidden
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B0F14]/70 via-[#0B0F14]/85 to-[#0B0F14]" aria-hidden />

        <section className="relative container max-w-5xl py-28 md:py-36 text-center">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-white/15 text-[11px] uppercase tracking-[0.25em] text-white/70 mb-8">
            <Building2 className="w-3.5 h-3.5" /> {es ? "Cadenas · Franquicias · Multipoint" : "Chains · Franchises · Multipoint"}
          </span>
          <h1 className="font-display text-4xl md:text-6xl font-semibold leading-[1.05] tracking-tight">
            {es ? "Escalar tu red de tiendas" : "Scaling your store network"} <br className="hidden md:block" />
            <span className="text-foreground">{es ? "no debería multiplicar" : "shouldn't multiply"}</span> {es ? "tus problemas." : "your problems."}
          </h1>
          <p className="mt-8 text-lg md:text-xl text-white/70 max-w-3xl mx-auto leading-relaxed font-light">
            {es
              ? <>Estandarizamos la operativa de tu cadena para que cada nuevo punto de venta sea <span className="text-white font-medium">rentable y eficiente</span> desde el día uno.</>
              : <>We standardize your chain's operations so every new point of sale is <span className="text-white font-medium">profitable and efficient</span> from day one.</>}
          </p>
          <a href="#contacto" className="inline-flex items-center gap-2 mt-12 text-sm uppercase tracking-[0.2em] text-white/80 hover:text-foreground transition-colors">
            {es ? "Solicitar auditoría operativa" : "Request operational audit"} <ArrowRight className="w-4 h-4" />
          </a>
        </section>
      </section>

      {/* Pillars */}
      <section className="py-24 md:py-32 border-t border-white/5">
        <section className="container max-w-6xl">
          <div className="text-center mb-20">
            <p className="text-[11px] uppercase tracking-[0.3em] text-foreground mb-4">{es ? "Nuestro enfoque" : "Our approach"}</p>
            <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight">
              {es ? "Tres palancas para una red consistente." : "Three levers for a consistent network."}
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {PILLARS.map((p) => (
              <div key={p.n} className="bg-[#0B0F14] p-10 md:p-12 group hover:bg-[#10161E] transition-colors">
                <div className="flex items-center justify-between mb-8">
                  <p.icon className="w-6 h-6 text-primary" strokeWidth={1.5} />
                  <span className="font-display text-xs text-white/30 tracking-widest">{p.n}</span>
                </div>
                <h3 className="font-display text-xl font-semibold mb-4 leading-snug">
                  {p.title}
                </h3>
                <p className="text-sm text-white/60 leading-relaxed font-light">
                  {p.desc}
                </p>
              </div>
            ))}
          </div>
        </section>
      </section>

      {/* Visual band */}
      <section className="relative h-[280px] md:h-[360px] overflow-hidden border-y border-white/5">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&w=1920&q=80')",
            backgroundSize: "cover",
            backgroundPosition: "center 40%",
          }}
          aria-hidden
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B0F14] via-[#0B0F14]/60 to-transparent" aria-hidden />
        <section className="relative container max-w-6xl h-full flex items-center">
          <p className="font-display text-2xl md:text-3xl max-w-2xl leading-snug text-white/90">
            {es
              ? <>“La diferencia entre una cadena rentable y una caótica nunca está en las personas. <span className="text-foreground">Está en el sistema.</span>”</>
              : <>“The difference between a profitable chain and a chaotic one is never the people. <span className="text-foreground">It's the system.</span>”</>}
          </p>
        </section>
      </section>

      {/* Contact form */}
      <section id="contacto" className="py-24 md:py-32">
        <section className="container max-w-2xl">
          {!sent ? (
            <>
              <div className="text-center mb-12">
                <p className="text-[11px] uppercase tracking-[0.3em] text-foreground mb-4">{es ? "Conversación inicial" : "Initial conversation"}</p>
                <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight">
                  {es ? "Solicita una auditoría operativa." : "Request an operational audit."}
                </h2>
                <p className="text-white/60 mt-4 font-light">
                  {es
                    ? "Cuéntanos sobre tu red y nuestro equipo te contactará para una primera evaluación sin compromiso."
                    : "Tell us about your network and our team will contact you for an initial no-obligation assessment."}
                </p>
              </div>

              <form onSubmit={handleSubmit} className="bg-[#10161E] border border-white/10 rounded-lg p-8 md:p-10 space-y-6">
                <div className="grid md:grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-white/60">{es ? "Nombre" : "Name"}</Label>
                    <Input
                      value={form.full_name}
                      onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                      className={`bg-[#0B0F14] border-white/10 text-white h-11 ${errors['full_name'] ? "border-destructive" : ""}`}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-white/60">{es ? "Cargo" : "Role"}</Label>
                    <Input
                      value={form.role}
                      onChange={(e) => setForm({ ...form, role: e.target.value })}
                      className={`bg-[#0B0F14] border-white/10 text-white h-11 ${errors['role'] ? "border-destructive" : ""}`}
                      placeholder={es ? "CEO, Director Operaciones..." : "CEO, Operations Director..."}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-white/60">{es ? "Empresa" : "Company"}</Label>
                  <Input
                    value={form.company}
                    onChange={(e) => setForm({ ...form, company: e.target.value })}
                    className={`bg-[#0B0F14] border-white/10 text-white h-11 ${errors['company'] ? "border-destructive" : ""}`}
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-white/60">{es ? "Número de tiendas actuales" : "Current number of stores"}</Label>
                  <Select value={form.store_count} onValueChange={(v) => setForm({ ...form, store_count: v })}>
                    <SelectTrigger className={`bg-[#0B0F14] border-white/10 text-white h-11 ${errors['store_count'] ? "border-destructive" : ""}`}>
                      <SelectValue placeholder={es ? "Selecciona un rango" : "Select a range"} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="5-10">{es ? "5 – 10 tiendas" : "5 – 10 stores"}</SelectItem>
                      <SelectItem value="11-30">{es ? "11 – 30 tiendas" : "11 – 30 stores"}</SelectItem>
                      <SelectItem value="+30">{es ? "Más de 30 tiendas" : "More than 30 stores"}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-white/60">{es ? "Email corporativo" : "Corporate email"}</Label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className={`bg-[#0B0F14] border-white/10 text-white h-11 ${errors['email'] ? "border-destructive" : ""}`}
                  />
                </div>

                <Button
                  type="submit"
                  disabled={sending}
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-12 font-semibold uppercase tracking-wider text-xs"
                >
                  {sending ? (es ? "Enviando..." : "Sending...") : (es ? "Solicitar Auditoría Operativa" : "Request Operational Audit")}
                </Button>

                <p className="flex items-center justify-center gap-2 text-xs text-white/50 pt-2">
                  <ShieldCheck className="w-3.5 h-3.5" />
                  🔒 {es ? "Tus datos están protegidos. Un socio director contactará contigo en menos de 24h." : "Your data is protected. A managing partner will contact you within 24h."}
                </p>
              </form>
            </>
          ) : (
            <div className="text-center py-20 bg-[#10161E] border border-white/10 rounded-lg">
              <CheckCircle2 className="w-14 h-14 text-foreground mx-auto mb-6" strokeWidth={1.5} />
              <h2 className="font-display text-2xl font-semibold mb-3">{es ? "Solicitud recibida." : "Request received."}</h2>
              <p className="text-white/60 max-w-md mx-auto">
                {es ? "Un socio director revisará tu caso y te contactará en menos de 24h." : "A managing partner will review your case and contact you within 24h."}
              </p>
            </div>
          )}
        </section>
      </section>

      {/* Footer mini */}
      <div className="border-t border-white/5">
        <section className="container max-w-6xl py-8 flex items-center justify-between text-xs text-white/40">
          <span>© Retail Lemon · Corporate Solutions</span>
          <Link to="/" className="hover:text-white transition-colors">{es ? "Volver a la web principal →" : "Back to main site →"}</Link>
        </section>
      </div>
    </div>
  );
}
